import { useState, useCallback, useRef, type RefObject } from 'react';
import type { ViewState } from '../types/warehouse.types';
import { DRAG_THRESHOLD } from '../constants/warehouse.constants';

interface UseCanvasTouchGesturesProps {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
}

/**
 * Utility: Calculate distance between two points
 */
const calculateDistance = (x1: number, y1: number, x2: number, y2: number): number => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Utility: Calculate angle between two points (in radians)
 */
const calculateAngle = (x1: number, y1: number, x2: number, y2: number): number => {
  return Math.atan2(y2 - y1, x2 - x1);
};

/**
 * Utility: Get position relative to canvas
 */
const getCanvasRelativePos = (clientX: number, clientY: number, rect: DOMRect): { x: number; y: number } => {
  return {
    x: clientX - rect.left,
    y: clientY - rect.top
  };
};

interface UseCanvasTouchGesturesResult {
  handleTouchStart: (e: React.TouchEvent<HTMLCanvasElement>) => void;
  handleTouchMove: (e: React.TouchEvent<HTMLCanvasElement>) => void;
  handleTouchEnd: (e: React.TouchEvent<HTMLCanvasElement>) => void;
  handleTouchCancel: (e: React.TouchEvent<HTMLCanvasElement>) => void;
  isTouching: boolean;
}

type GestureType = 'none' | 'pan' | 'pinch-rotate';

/**
 * Hook for handling touch gestures on canvas (pinch-to-zoom, single-finger panning, two-finger rotation)
 *
 * Gesture priority:
 * - One finger: panning after exceeding drag threshold
 * - Two fingers: simultaneous pinch-to-zoom and rotation
 *
 * Browser zoom is prevented when touching the canvas via preventDefault on touch events.
 */
export const useCanvasTouchGestures = ({
  canvasRef,
  setViewState
}: UseCanvasTouchGesturesProps): UseCanvasTouchGesturesResult => {
  const [isTouching, setIsTouching] = useState(false);
  const [touchStartPos, setTouchStartPos] = useState<{ x: number; y: number } | null>(null);
  const [lastTouchPos, setLastTouchPos] = useState<{ x: number; y: number } | null>(null);
  const [lastTouchDistance, setLastTouchDistance] = useState<number | null>(null);
  const [lastTouchAngle, setLastTouchAngle] = useState<number | null>(null);
  const [touchMoveDistance, setTouchMoveDistance] = useState(0);
  const [gestureType, setGestureType] = useState<GestureType>('none');

  // requestAnimationFrame throttling for touch move
  const rafId = useRef<number | null>(null);

  /**
   * Handle touch start event
   */
  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    // Prevent browser zoom
    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const touchCount = e.touches.length;

    if (touchCount === 1) {
      // Single finger - prepare for panning
      const pos = getCanvasRelativePos(e.touches[0].clientX, e.touches[0].clientY, rect);
      setIsTouching(true);
      setTouchStartPos(pos);
      setLastTouchPos(pos);
      setTouchMoveDistance(0);
      setGestureType('none'); // Wait for threshold
    } else if (touchCount === 2) {
      // Two fingers - pinch and rotate
      const touch0 = e.touches[0];
      const touch1 = e.touches[1];

      const distance = calculateDistance(touch0.clientX, touch0.clientY, touch1.clientX, touch1.clientY);
      const angle = calculateAngle(touch0.clientX, touch0.clientY, touch1.clientX, touch1.clientY);

      setIsTouching(true);
      setLastTouchDistance(distance);
      setLastTouchAngle(angle);
      setGestureType('pinch-rotate');
    }
    // Ignore 3+ fingers
  }, [canvasRef]);

  /**
   * Handle touch move event
   */
  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    // Prevent browser zoom
    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;

    // IMPORTANT: Save touch data BEFORE requestAnimationFrame
    // React reuses synthetic events, so e.touches will be null inside RAF callback
    const touchCount = e.touches.length;
    const touch0 = touchCount >= 1 ? { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY } : null;
    const touch1 = touchCount >= 2 ? { clientX: e.touches[1].clientX, clientY: e.touches[1].clientY } : null;

    // Throttle with requestAnimationFrame for performance
    if (rafId.current !== null) return;

    rafId.current = requestAnimationFrame(() => {
      const rect = canvas.getBoundingClientRect();

      // Single finger panning
      if (touchCount === 1 && gestureType !== 'pinch-rotate' && touch0) {
        const currentPos = getCanvasRelativePos(touch0.clientX, touch0.clientY, rect);
        const referencePos = lastTouchPos || touchStartPos;

        if (referencePos) {
          const dx = currentPos.x - referencePos.x;
          const dy = currentPos.y - referencePos.y;

          // Accumulate movement distance
          const distance = Math.sqrt(dx * dx + dy * dy);
          const newMoveDistance = touchMoveDistance + distance;
          setTouchMoveDistance(newMoveDistance);

          // Check threshold
          if (newMoveDistance > DRAG_THRESHOLD) {
            setGestureType('pan');
          }

          // Apply panning if threshold exceeded
          if (gestureType === 'pan' || newMoveDistance > DRAG_THRESHOLD) {
            setViewState(prev => {
              // Apply rotation-aware panning (same math as useCanvasPanning)
              const cos = Math.cos(-prev.rotation);
              const sin = Math.sin(-prev.rotation);
              const rotatedDx = dx * cos - dy * sin;
              const rotatedDy = dx * sin + dy * cos;

              return {
                ...prev,
                offsetX: prev.offsetX + rotatedDx / prev.scale,
                offsetY: prev.offsetY + rotatedDy / prev.scale
              };
            });
          }

          setLastTouchPos(currentPos);
        }
      }

      // Two finger pinch and rotate
      if (touchCount === 2 && gestureType === 'pinch-rotate' && touch0 && touch1) {
        const currentDistance = calculateDistance(touch0.clientX, touch0.clientY, touch1.clientX, touch1.clientY);
        const currentAngle = calculateAngle(touch0.clientX, touch0.clientY, touch1.clientX, touch1.clientY);

        // Calculate center between touches
        const centerX = (touch0.clientX + touch1.clientX) / 2;
        const centerY = (touch0.clientY + touch1.clientY) / 2;
        const center = getCanvasRelativePos(centerX, centerY, rect);

        if (lastTouchDistance && lastTouchAngle) {
          // Calculate deltas
          const scaleDelta = currentDistance / lastTouchDistance;
          const rotationDelta = currentAngle - lastTouchAngle;

          setViewState(prev => {
            // Apply pinch-to-zoom (same math as useViewTransform wheel zoom)
            const newScale = Math.max(0.1, Math.min(10, prev.scale * scaleDelta));

            // Zoom relative to touch center - convert center from screen space to world space
            const worldCenterX = center.x / prev.scale - prev.offsetX;
            const worldCenterY = center.y / prev.scale - prev.offsetY;

            // Calculate new offset to keep center at the same world position
            const newOffsetX = center.x / newScale - worldCenterX;
            const newOffsetY = center.y / newScale - worldCenterY;

            // Apply rotation
            const newRotation = prev.rotation + rotationDelta;

            return {
              scale: newScale,
              offsetX: newOffsetX,
              offsetY: newOffsetY,
              rotation: newRotation
            };
          });
        }

        setLastTouchDistance(currentDistance);
        setLastTouchAngle(currentAngle);
      }

      // Handle transition from 2 fingers to 1 finger
      if (touchCount === 1 && gestureType === 'pinch-rotate' && touch0) {
        const pos = getCanvasRelativePos(touch0.clientX, touch0.clientY, rect);
        setTouchStartPos(pos);
        setLastTouchPos(pos);
        setTouchMoveDistance(0);
        setGestureType('none');
        setLastTouchDistance(null);
        setLastTouchAngle(null);
      }

      rafId.current = null;
    });
  }, [
    canvasRef,
    gestureType,
    touchStartPos,
    lastTouchPos,
    touchMoveDistance,
    lastTouchDistance,
    lastTouchAngle,
    setViewState
  ]);

  /**
   * Handle touch end event
   */
  const handleTouchEnd = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    const touchCount = e.touches.length;

    if (touchCount === 0) {
      // All fingers lifted - reset state
      setIsTouching(false);
      setTouchStartPos(null);
      setLastTouchPos(null);
      setTouchMoveDistance(0);
      setGestureType('none');
      setLastTouchDistance(null);
      setLastTouchAngle(null);
    } else if (touchCount === 1 && gestureType === 'pinch-rotate') {
      // Transition from 2 fingers to 1 finger - switch to panning mode
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const pos = getCanvasRelativePos(e.touches[0].clientX, e.touches[0].clientY, rect);

      setTouchStartPos(pos);
      setLastTouchPos(pos);
      setTouchMoveDistance(0);
      setGestureType('none');
      setLastTouchDistance(null);
      setLastTouchAngle(null);
    } else if (touchCount === 2) {
      // Transition from 3+ fingers to 2 fingers - re-initialize two-finger gesture
      const touch0 = e.touches[0];
      const touch1 = e.touches[1];

      const distance = calculateDistance(touch0.clientX, touch0.clientY, touch1.clientX, touch1.clientY);
      const angle = calculateAngle(touch0.clientX, touch0.clientY, touch1.clientX, touch1.clientY);

      setLastTouchDistance(distance);
      setLastTouchAngle(angle);
      setGestureType('pinch-rotate');
    }
  }, [canvasRef, gestureType]);

  /**
   * Handle touch cancel event (e.g., OS interrupts gesture)
   */
  const handleTouchCancel = useCallback(() => {
    // Reset all state
    setIsTouching(false);
    setTouchStartPos(null);
    setLastTouchPos(null);
    setTouchMoveDistance(0);
    setGestureType('none');
    setLastTouchDistance(null);
    setLastTouchAngle(null);

    // Cancel any pending RAF
    if (rafId.current !== null) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;
    }
  }, []);

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleTouchCancel,
    isTouching
  };
};
